import BaseApiService from "./BaseApiService";
import {
    DocumentFile,
    DocumentFileListIO,
    DocumentFolder,
    DocumentFolderListIO,
    Progress,
    ProgressIO,
    DocumentsCreateResponse,
    DocumentsCreateResponseIO,
    GroupedError,
    GroupedErrorListIO,
} from "../models/Document";
import {
    TemplateFile,
    TemplateFileListIO,
    TemplateUploadResponse,
    TemplateUploadResponseIO,
} from "../models/Templates";
import { TemplateMeta, TemplateMetaIO } from "../models/TemplateMeta";
import {
    EventListIO,
    Event,
    EventInfo,
    EventInfoIO,
    EventGuestStatistic,
    EventGuestStatisticIO,
} from "../models/Event";
import { FieldMappings, OutputSettings } from "../components/modals/createwizard/useCreateWizard";
import { unwrapDecoding } from "../utils/unwrap";
import logger from "../utils/logging";

export type TemplateData = {
    eventId: string;
    templateUrl: string;
};

export type DocumentData = {
    eventId: string;
    templateId: string;
    replacementConfig: FieldMappings;
    // Optional per-run overrides for QR code content (keyed by QR code id).
    // If absent or if a QR code id is not present, the template's stored content is used.
    qrCodeContentOverrides: Record<string, string> | null;
    outputSettings: OutputSettings;
};

export type AuthVerificationResponse = {
    authenticated: boolean;
    isTrialMode: boolean;
    user: {
        username?: string;
        email?: string;
    };
};

/**
 * ApiService - Specific API endpoints for the PDF Form Filler application
 * Extends BaseApiService with domain-specific methods
 */
export default class ApiService extends BaseApiService {
    // ============== AUTHENTICATION METHODS ==============

    /**
     * Verify authentication with backend
     */
    async verifyAuthentication(): Promise<AuthVerificationResponse> {
        const response = await this.get("/auth/verify");
        logger.logObject("info", "verifyAuthentication", response);
        return response as AuthVerificationResponse;
    }

    // ============== TEMPLATE METHODS ==============

    /**
     * Get all templates
     */
    async getTemplates(): Promise<TemplateFile[]> {
        const response = await this.get("/templates");
        return unwrapDecoding(TemplateFileListIO.decode(response));
    }

    /**
     * Create a new template
     */
    async createTemplate(templateData: TemplateData): Promise<unknown> {
        return this.post("/templates", templateData);
    }

    /**
     * Upload a template file
     */
    async uploadTemplate(formData: FormData): Promise<TemplateUploadResponse> {
        const response = await this.upload("/templates/upload", formData);
        return unwrapDecoding(TemplateUploadResponseIO.decode(response));
    }

    /**
     * Delete a template by ID
     */
    async deleteTemplate(templateId: string): Promise<unknown> {
        return this.delete(`/templates/${templateId}`);
    }

    /**
     * Fetch a template PDF as Blob
     */
    async getTemplatePdf(templateId: string): Promise<Blob> {
        return this.getBlob(`/templates/${templateId}/pdf`, {
            Accept: "application/pdf",
        });
    }

    // ============== TEMPLATE META (PLACEHOLDER) ==============

    async getTemplateMeta(templateId: string): Promise<TemplateMeta> {
        const response = await this.get(`/templates/${templateId}/meta`);
        return unwrapDecoding(TemplateMetaIO.decode(response));
    }

    async saveTemplateMeta(templateId: string, meta: TemplateMeta): Promise<TemplateMeta> {
        const response = await this.put(`/templates/${templateId}/meta`, meta);
        return unwrapDecoding(TemplateMetaIO.decode(response));
    }

    // ============== DOCUMENT METHODS ==============

    /**
     * Get all document folders (batch folders)
     */
    async getDocumentFolders(): Promise<DocumentFolder[]> {
        const response = await this.get("/documentfolders");
        return unwrapDecoding(DocumentFolderListIO.decode(response));
    }

    /**
     * Get files in a specific document folder (generated PDF files)
     */
    async getDocumentFiles(folder: DocumentFolder): Promise<DocumentFile[]> {
        const response = await this.get(`/documentfolders/${folder.id}/files`);
        return unwrapDecoding(DocumentFileListIO.decode(response));
    }

    /**
     * Get grouped errors for a specific document folder
     */
    async getDocumentErrors(folder: DocumentFolder): Promise<GroupedError[]> {
        const response = await this.get(`/documentfolders/${folder.id}/errors`);
        return unwrapDecoding(GroupedErrorListIO.decode(response));
    }

    /**
     * Create documents
     */
    async createDocuments(documentData: DocumentData): Promise<DocumentsCreateResponse> {
        const response = await this.post("/document/create", documentData);
        return unwrapDecoding(DocumentsCreateResponseIO.decode(response));
    }

    /**
     * Get document creation progress
     */
    async getDocumentProgress(progressId: string): Promise<Progress> {
        const response = await this.get(`/document/create/${progressId}/progress`);
        return unwrapDecoding(ProgressIO.decode(response));
    }

    /**
     * Delete document creation progress
     */
    async deleteDocumentProgress(progressId: string): Promise<unknown> {
        return this.delete(`/document/create/${progressId}/progress`);
    }

    /**
     * Delete document folder
     */
    async deleteDocumentFolder(folderId: string): Promise<unknown> {
        return this.delete(`/documentfolders/${folderId}`);
    }

    // ============== EVENT METHODS ==============

    /**
     * Get all events.
     */
    async getEvents(ignoreArchived = false): Promise<Event[]> {
        const params = ignoreArchived ? "?ignoreArchived=true" : "";
        const response = await this.get(`/events${params}`);
        return unwrapDecoding(EventListIO.decode(response));
    }

    /**
     * Get event information by ID
     */
    async getEventInfo(eventId: string): Promise<EventInfo> {
        const response = await this.get(`/events/${eventId}/info`);
        return unwrapDecoding(EventInfoIO.decode(response));
    }

    /**
     * Get event guest statistics by ID
     */
    async getEventGuestStatistic(eventId: string): Promise<EventGuestStatistic> {
        const response = await this.get(`/events/${eventId}/guest-statistic`);
        return unwrapDecoding(EventGuestStatisticIO.decode(response));
    }
}
